/**
 * Report Builder Page
 * Allows users to generate and create reports from selected data
 */

import React, { useState, useCallback } from 'react';
import { Button, Text } from '@fluentui/react-components';
import { DocumentSearchRegular, ArrowResetRegular } from '@fluentui/react-icons';
import type { ReportPlan } from '@excel-ai-studio/shared';
import { generateReportPlan } from '../services/api.client';
import { readSelectionInfo, type SelectionInfo } from '../office';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { ReportPreviewPanel } from '../components/features/ReportPreviewPanel';
import './ReportBuilderPage.css';

export const ReportBuilderPage: React.FC = () => {
  // Selection
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [selectionLoading, setSelectionLoading] = useState(false);

  // Plan generation
  const [plan, setPlan] = useState<ReportPlan>();
  const [planLoading, setPlanLoading] = useState(false);

  // Report creation
  const [creationLoading, setCreationLoading] = useState(false);

  // Shared
  const [error, setError] = useState<string>();

  // ─── Helpers ─────────────────────────────────────────

  const resetAll = () => {
    setSelectionInfo(undefined);
    setPlan(undefined);
    setError(undefined);
  };

  const isSelectionEmpty = (values: unknown[][]): boolean =>
    values.every((row) =>
      row.every((cell) => cell === null || cell === undefined || String(cell).trim() === ''),
    );

  // ─── Step 1: Read selection ──────────────────────────

  const handleReadSelection = async () => {
    setError(undefined);
    setPlan(undefined);
    setSelectionLoading(true);

    try {
      const info = await readSelectionInfo();

      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule sélectionnée. Sélectionnez des données dans Excel puis réessayez.');
        setSelectionInfo(undefined);
        return;
      }

      if (isSelectionEmpty(info.values)) {
        setError('La sélection ne contient que des cellules vides.');
        setSelectionInfo(undefined);
        return;
      }

      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setSelectionLoading(false);
    }
  };

  // ─── Step 2: Generate plan ──────────────────────────

  const handleGeneratePlan = useCallback(async () => {
    if (!selectionInfo) return;

    setError(undefined);
    setPlanLoading(true);

    try {
      // Extract headers from first row if available
      const headers = selectionInfo.values.length > 0
        ? (selectionInfo.values[0] as unknown[]).map((v) => String(v ?? ''))
        : undefined;

      const response = await generateReportPlan({
        values: selectionInfo.values,
        headers,
      });

      setPlan(response.plan);
    } catch (err) {
      setError((err as Error).message);
      setPlan(undefined);
    } finally {
      setPlanLoading(false);
    }
  }, [selectionInfo]);

  // ─── Step 3: Create report ──────────────────────────

  const handleCreateReport = useCallback(async () => {
    if (!plan || !selectionInfo) return;

    setError(undefined);
    setCreationLoading(true);

    try {
      const { createReportSheet } = await import('../services/report-creator.service');

      const result = await createReportSheet({
        plan,
        values: selectionInfo.values,
        headers: selectionInfo.values.length > 0
          ? (selectionInfo.values[0] as unknown[]).map((v) => String(v ?? ''))
          : undefined,
        sheetName: plan.title,
      });

      if (result.success) {
        setError(undefined);
        // Reset after successful creation
        setTimeout(() => {
          resetAll();
        }, 1000);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreationLoading(false);
    }
  }, [plan, selectionInfo]);

  // ─── Render ──────────────────────────────────────────

  return (
    <div className="report-builder-page">
      {/* Error Display */}
      {error && <ErrorDisplay message={error} />}

      {/* Step 1: Read Selection */}
      {!selectionInfo && !plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 1: Sélectionner les données
          </Text>
          <Text size={300}>
            Sélectionnez un tableau de données dans Excel pour générer un rapport analytique automatique.
          </Text>
          <Button
            appearance="primary"
            size="large"
            icon={<DocumentSearchRegular />}
            onClick={handleReadSelection}
            disabled={selectionLoading}
          >
            {selectionLoading ? 'Lecture en cours...' : 'Lire la sélection'}
          </Button>
        </div>
      )}

      {/* Step 2: Generate Plan */}
      {selectionInfo && !plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 2: Générer le plan du rapport
          </Text>
          <Text size={300}>
            {selectionInfo.values.length} lignes × {selectionInfo.values[0]?.length || 0} colonnes détectées
          </Text>
          <div className="button-group">
            <Button
              appearance="primary"
              size="large"
              onClick={handleGeneratePlan}
              disabled={planLoading}
            >
              {planLoading ? 'Génération en cours...' : 'Générer le plan'}
            </Button>
            <Button
              appearance="secondary"
              size="large"
              icon={<ArrowResetRegular />}
              onClick={resetAll}
            >
              Recommencer
            </Button>
          </div>
          {planLoading && <LoadingSpinner />}
        </div>
      )}

      {/* Step 3: Preview and Create */}
      {plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 3: Aperçu et création
          </Text>
          <ReportPreviewPanel
            plan={plan}
            onGenerate={handleCreateReport}
            isGenerating={creationLoading}
          />
          <Button
            appearance="secondary"
            size="large"
            icon={<ArrowResetRegular />}
            onClick={resetAll}
            disabled={creationLoading}
          >
            Recommencer
          </Button>
        </div>
      )}
    </div>
  );
};
